import Rollbar from 'rollbar'
import config from 'shared/config'
import logger from 'shared/logger'
import { ROLLBAR_HOST_SAFE_LIST, rollbarCheckIgnore } from 'shared/rollbarIgnoreRules'
import { isClientEnvironment } from 'shared/utils'

let serverRollbar: Rollbar
let clientRollbar: Rollbar

const log = logger().child({
  namespace: 'errorReporter'
})

const canReportErrorToErrorReporter = config('APP_ENV') === 'production' || config('APP_ENV') === 'staging'

const rollbarBaseConfig: Rollbar.Configuration = {
  accessToken: isClientEnvironment()
    ? (config('NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN') as string)
    : (config('ROLLBAR_SERVER_TOKEN') as string),
  captureUncaught: true,
  captureUnhandledRejections: true,
  environment: config('APP_ENV') as string
}

const rollbarBrowserConfig: Rollbar.Configuration = {
  ...rollbarBaseConfig,
  hostSafeList: ROLLBAR_HOST_SAFE_LIST,
  checkIgnore: rollbarCheckIgnore,
  autoInstrument: {
    network: true,
    log: true,
    dom: true,
    navigation: true,
    connectivity: true,
    contentSecurityPolicy: true,
    errorOnContentSecurityPolicy: false
  },
  payload: {
    environment: config('APP_ENV') as string,
    client: {
      javascript: {
        source_map_enabled: true,
        code_version: config('NEXT_PUBLIC_VERSION') as string,
        guess_uncaught_frames: true
      }
    }
  }
}

const rollbarServerConfig: Rollbar.Configuration = {
  ...rollbarBaseConfig,
  payload: {
    environment: config('APP_ENV') as string,
    server: {
      code_version: config('NEXT_PUBLIC_VERSION') as string
    }
  }
}
// In the browser, every Rollbar instance installs its own window.onerror. A server-config
// instance there would report uncaught errors without the browser checkIgnore rules.
if (canReportErrorToErrorReporter) {
  if (isClientEnvironment()) {
    clientRollbar = new Rollbar(rollbarBrowserConfig)
  } else {
    serverRollbar = new Rollbar(rollbarServerConfig)
  }
}

export const rollbarConfig = isClientEnvironment() ? rollbarBrowserConfig : rollbarServerConfig

export const logAndReportError = (err: Error): void => {
  log.error(err.message)
  if (canReportErrorToErrorReporter) {
    const rollbar = isClientEnvironment() ? clientRollbar : serverRollbar
    try {
      rollbar.error(err)
    } catch (e) {
      log.error(`Reporting error failed: ${(e as Error).message}`)
    }
  }
}

export const transform = ({ sessionUrl }: { sessionUrl: string }): void => {
  try {
    clientRollbar.configure({
      transform: (payload: Rollbar.Payload) => {
        payload.sessionUrl = sessionUrl
      }
    })
  } catch (e) {
    log.error(`Error transforming payload: ${(e as Error).message}`)
  }
}

export const getRollbarInstance = (): Rollbar | undefined => {
  if (!canReportErrorToErrorReporter) return
  return isClientEnvironment() ? clientRollbar : serverRollbar
}
